/**
 * Route Error Boundary
 *
 * Catches React Router errors and displays appropriate UI
 * - Shows 404 for routing errors
 * - Shows error screen for component errors
 * - Logs all errors for monitoring
 * - Reports errors to Sentry
 */

import { useRouteError, isRouteErrorResponse } from "react-router-dom";
import { useEffect } from "react";
import PageNotFound from "../../../pages/Others/PageNotFound";
import ErrorBoundary from "../ErrorBoundary";
import Logger from "../../../utils/logger";
import { captureException, addBreadcrumb } from "../../../utils/sentry";

const RouteErrorBoundary = () => {
  const error = useRouteError();

  // Log and report routing errors
  useEffect(() => {
    if (!error) return;

    // Log the error
    Logger.error("Route Error Caught:", error);

    // Add breadcrumb for context
    addBreadcrumb({
      category: "routing",
      message: `Route error: ${
        isRouteErrorResponse(error)
          ? `${error.status} ${error.statusText}`
          : error instanceof Error
          ? error.message
          : String(error)
      }`,
      level:
        isRouteErrorResponse(error) && error.status === 404 ? "info" : "error",
      data: isRouteErrorResponse(error)
        ? {
            status: error.status,
            statusText: error.statusText,
            data: error.data,
          }
        : undefined,
    });

    // Report to Sentry (but don't spam with 404s in production)
    const shouldReport =
      !isRouteErrorResponse(error) ||
      error.status !== 404 ||
      process.env.NODE_ENV === "development";

    if (shouldReport) {
      captureException(
        error instanceof Error ? error : new Error(String(error)),
        {
          tags: {
            "error.type": "routing",
            "error.status": isRouteErrorResponse(error)
              ? String(error.status)
              : "unknown",
          },
          level:
            isRouteErrorResponse(error) && error.status === 404
              ? "info"
              : "error",
          extra: isRouteErrorResponse(error)
            ? {
                statusText: error.statusText,
                data: error.data,
              }
            : undefined,
        }
      );
    }
  }, [error]);

  // Handle 404 errors with PageNotFound component
  if (isRouteErrorResponse(error) && error.status === 404) {
    return <PageNotFound />;
  }

  // For all other errors (component crashes, network errors, etc), show error screen
  return (
    <ErrorBoundary>
      {/* Trigger the error boundary */}
      <ErrorThrower error={error} />
    </ErrorBoundary>
  );
};

// Helper component to throw the error so ErrorBoundary catches it
const ErrorThrower = ({ error }: { error: unknown }) => {
  if (error instanceof Error) {
    throw error;
  }
  throw new Error(String(error));
};

export default RouteErrorBoundary;
